// src/stores/payroll.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from './auth.js'
import { obHoursApi } from '../services/obHoursApi.js'
import { shiftApi } from '../services/shiftApi.js'
import { sickLeaveApi } from '../services/sickLeaveApi.js'
import { timeEntryApi } from '../services/timeEntryApi.js'
import { calculatePreliminaryTax } from '../services/taxService.js'
import { usePayrollCalculations } from '../composables/usePayrollCalculations.js'
import { 
  monthlyHours as calculateMonthlyHours,
  useTimeCalculations,
  calculateAllHours,
  requiresLegalBreak,
  calculateBreakTime,
  calculateRequiredBreak
} from '../composables/useTimeCalculations.js'

export const usePayrollStore = defineStore('payroll', () => {
  const authStore = useAuthStore()
  const payroll = usePayrollCalculations()
  const { calculateTotalHours, formatQuarterHours } = useTimeCalculations()
  
  // Centralized payroll data for all users for the selected month
  const selectedMonth = ref(new Date())
  const loading = ref(false)
  
  // 🚀 OPTIMIZATION: Request deduplication and caching
  const loadingPromises = new Map() // Prevent duplicate API calls
  const dataCache = new Map() // Cache API responses by month-year key
  
    // Monthly data maps: { userId: data }
  const monthlyShiftData = ref({}) // { userId: { "2024-10-15": [shift1, shift2], "2024-10-16": [shift3] } }
  const monthlyTimeEntryData = ref({}) // { userId: { "2024-10-15": [entry1, entry2], "2024-10-16": [entry3] } }
  const monthlyOBHours = ref({})
  const monthlySickLeaves = ref({})
  const monthlyWorkDays = ref({})
  const userTaxCache = ref({})
  
  // Computed values using unified calculateMonthlyHours function
  const monthlyAssignedHours = computed(() => {
    // Create explicit dependency on selectedMonth to ensure reactivity
    const currentMonth = selectedMonth.value
    const shiftData = monthlyShiftData.value
    
    // Force recalculation when month changes
    const result = {}
    Object.keys(shiftData).forEach(userId => {
      result[userId] = calculateMonthlyHours(shiftData[userId], currentMonth)
    })
    return result
  })
  
  const monthlyReportedHours = computed(() => {
    // Create explicit dependency on selectedMonth to ensure reactivity
    const currentMonth = selectedMonth.value
    const timeEntryData = monthlyTimeEntryData.value
    
    // Force recalculation when month changes
    const result = {}
    Object.keys(timeEntryData).forEach(userId => {
      result[userId] = calculateMonthlyHours(timeEntryData[userId], currentMonth)
    })
    return result
  })
  
  // OB Coefficients from company settings
  const obCoefficients = computed(() => {
    const company = authStore.company
    return {
      ob1: Number(company?.overtime_rate1) ?? 0.3,
      ob2: Number(company?.overtime_rate2) ?? 0.5,
      ob3: Number(company?.overtime_rate3) ?? 1.0,
    }
  })
  
  // Store users for calculations
  const users = ref([])
  
  // 🚀 OPTIMIZATION: O(1) user lookup with Map cache
  const userMap = computed(() => {
    const map = new Map()
    users.value.forEach(user => map.set(user.id, user))
    return map
  })
  
  // Helper to get user by ID - now O(1) instead of O(n)
  function getUserById(userId) {
    return userMap.value.get(userId) || null
  }
  
  // 🚀 OPTIMIZATION: Memoized user data calculation
  const userDataCache = computed(() => {
    const cache = new Map()
    users.value.forEach(user => {
      const userId = user.id
      const assignedHours = monthlyAssignedHours.value[userId] || 0
      const reportedHours = monthlyReportedHours.value[userId] || 0
      const totalHours = Math.max(assignedHours, reportedHours) // Use MAX for call-ins
      const obHours = monthlyOBHours.value[userId] || { ob1_hours: 0, ob2_hours: 0, ob3_hours: 0 }
      const sickLeaves = monthlySickLeaves.value[userId] || []
      const workDays = monthlyWorkDays.value[userId] || []
      
      cache.set(userId, {
        user,
        totalHours,
        obHours,
        sickLeaves,
        workDays
      })
    })
    return cache
  })

  

  // STEP-BY-STEP TESTING: Step 2 - Fetch reported hours from time entries (using unified calculation)
  async function testStep2_FetchReportedHours(userId) {
    const year = selectedMonth.value.getFullYear()
    const month = selectedMonth.value.getMonth() + 1
    try {
      // Use the unified monthlyReportedHours computed value
      const reportedHours = monthlyReportedHours.value[userId] || 0
      
      // Also show the underlying data for debugging
      const userTimeEntryData = monthlyTimeEntryData.value[userId] || {}
      const entryDaysCount = Object.keys(userTimeEntryData).length
      const totalEntries = Object.values(userTimeEntryData).reduce((sum, dayEntries) => sum + dayEntries.length, 0)
      
      return reportedHours
    } catch (error) {
      console.error(`Error fetching reported hours for user ${userId}:`, error)
      return 0
    }
  }

  // STEP-BY-STEP TESTING: Step 3 - Calculate Base Salary
  async function testStep3_CalculateBaseSalary(userId) {
    // Get user data
    const user = getUserById(userId)
    if (!user) {
      console.error(`User ${userId} not found`)
      return 0
    }
    
    const employmentType = user.payroll?.employment_type || 'unknown'
    if (employmentType === 'call_in') {
      // CALL-IN CALCULATION
      const assignedHours = monthlyAssignedHours.value[userId] || 0
      const reportedHours = await testStep2_FetchReportedHours(userId)
      const totalHours = Math.max(assignedHours, reportedHours)
      const hourlyRate = user.payroll?.hourly_salary || 0
      const baseSalary = totalHours * hourlyRate
      
      return baseSalary
      
    } else if (employmentType === 'part_time' || employmentType === 'full_time') {
      // FIXED SALARY CALCULATION
      const monthlySalary = user.payroll?.monthly_salary || 0
      return monthlySalary
      
    } else {
      
      const assignedHours = monthlyAssignedHours.value[userId] || 0
      const reportedHours = await testStep2_FetchReportedHours(userId)
      const totalHours = Math.max(assignedHours, reportedHours)
      const hourlyRate = user.payroll?.hourly_salary || 0
      const baseSalary = totalHours * hourlyRate
      
      return baseSalary
    }
  }

  // UNIFIED CALCULATION TEST: Compare payroll store vs direct calculation
  async function testUnifiedCalculation(userId) {
    const currentMonth = selectedMonth.value
    // Test 1: Direct calculation using monthlyHours function
    const userShiftData = monthlyShiftData.value[userId] || {}
    const userTimeEntryData = monthlyTimeEntryData.value[userId] || {}
    
    const directAssignedHours = calculateMonthlyHours(userShiftData, selectedMonth.value)
    const directReportedHours = calculateMonthlyHours(userTimeEntryData, selectedMonth.value)
    
    // Test 2: Computed values from payroll store
    const computedAssignedHours = monthlyAssignedHours.value[userId] || 0
    const computedReportedHours = monthlyReportedHours.value[userId] || 0
    
    // Test 3: Compare results
    const assignedMatch = Math.abs(directAssignedHours - computedAssignedHours) < 0.01
    const reportedMatch = Math.abs(directReportedHours - computedReportedHours) < 0.01
    
    // Test 4: Final base salary calculation
    const baseSalary = getUserBaseSalary(userId)
    
    return {
      assignedMatch,
      reportedMatch,
      baseSalary,
      directAssignedHours,
      directReportedHours,
      computedAssignedHours,
      computedReportedHours,
      currentMonth: `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`
    }
  }

  // TEST MONTH CHANGES: Verify reactivity when month changes
  async function testMonthChange(usersList, userId = 3) {
    // Test current month first
    let result1 = await testUnifiedCalculation(userId)
    // Change to previous month
    const prevMonth = new Date(selectedMonth.value)
    prevMonth.setMonth(prevMonth.getMonth() - 1)
    await setSelectedMonth(prevMonth, usersList)
    
    let result2 = await testUnifiedCalculation(userId)
    
    // Change back to current month
    const currentMonth = new Date()
    await setSelectedMonth(currentMonth, usersList)
    
    let result3 = await testUnifiedCalculation(userId)
    
    // Verify the values changed and then came back
    const monthChangeWorking = result1.baseSalary !== result2.baseSalary || Math.abs(result1.baseSalary - result3.baseSalary) < 0.01
    
    return {
      monthChangeWorking,
      results: [result1, result2, result3]
    }
  }

  // Base salary calculation - PRODUCTION using unified calculateMonthlyHours
  function getUserBaseSalary(userId) {
    const user = getUserById(userId)
    if (!user || !user.payroll) return 0
    
    const employmentType = user.payroll.employment_type
    
    // EMPLOYMENT TYPE LOGIC:
    // - Call-ins: Use MAX(assigned, reported) hours × hourly rate
    // - Part-timers & Full-timers: Use fixed monthly salary
    
    if (employmentType === 'call_in') {
      // CALL-IN WORKERS: Hourly calculation
      
      // Step 1: Get assigned and reported hours
      const assignedHours = monthlyAssignedHours.value[userId] || 0
      const reportedHours = monthlyReportedHours.value[userId] || 0
      
      // Step 2: Use MAX for call-ins (they get paid for extra work)
      const totalHours = Math.max(assignedHours, reportedHours)
      
      // Step 3: Deduct sick leave hours (unpaid for call-ins)
      const userSickLeaves = monthlySickLeaves.value[userId] || []
      let sickLeaveHours = 0
      userSickLeaves.forEach(sl => {
        sickLeaveHours += sl.hours || 0
      })
      
      // Step 4: Calculate payable hours
      const payableHours = Math.max(0, totalHours - sickLeaveHours)
      
      // Step 5: Apply hourly rate
      const hourlyRate = user.payroll.hourly_salary || 0
      
      return payableHours * hourlyRate
      
    } else if (employmentType === 'part_time' || employmentType === 'full_time') {
      // PART-TIME & FULL-TIME: Fixed monthly salary
      
      return user.payroll.monthly_salary || 0
      
    } else {
      // Unknown employment type - default to hourly calculation
      console.warn(`Unknown employment type '${employmentType}' for user ${userId}. Defaulting to hourly calculation.`)
      
      const assignedHours = monthlyAssignedHours.value[userId] || 0
      const reportedHours = monthlyReportedHours.value[userId] || 0
      const totalHours = Math.max(assignedHours, reportedHours)
      const hourlyRate = user.payroll.hourly_salary || 0
      
      return totalHours * hourlyRate
    }
  }
  
  function getUserGrossSalary(userId) {
    const userData = userDataCache.value.get(userId)
    if (!userData) return 0
    
    return payroll.calculateMonthlySalary(
      userData.user,
      userData.totalHours,
      userData.obHours,
      obCoefficients.value,
      userData.sickLeaves,
      userData.workDays,
      userData.shiftHours
    )
  }
  
  // 🚀 OPTIMIZATION: Cached employer tax rates by user
  const employerTaxRates = computed(() => {
    const rates = new Map()
    users.value.forEach(user => {
      let taxRate = 0.3142 // Default 31.42%
      
      if (user.date_of_birth) {
        const birthYear = new Date(user.date_of_birth).getFullYear()
        
        // Born before 1959 or between 2006-2008: 10.21%
        if (birthYear < 1959 || (birthYear >= 2006 && birthYear <= 2008)) {
          taxRate = 0.1021
        }
      }
      
      rates.set(user.id, taxRate)
    })
    return rates
  })

  function getUserEmployeeTax(userId) {
    const userData = userDataCache.value.get(userId)
    if (!userData) return 0
    
    return payroll.calculateEmployeeTax(userData.user, userTaxCache.value, selectedMonth.value)
  }
  
  function getUserEmployerTax(userId) {
    const grossSalary = getUserGrossSalary(userId)
    const taxRate = employerTaxRates.value.get(userId)
    
    if (!grossSalary || !taxRate) return 0
    
    return grossSalary * taxRate
  }
  
  function getUserNetSalary(userId) {
    return getUserGrossSalary(userId) - getUserEmployeeTax(userId)
  }
  
  function getUserOBHours(userId) {
    return monthlyOBHours.value[userId] || { ob1_hours: 0, ob2_hours: 0, ob3_hours: 0 }
  }
  
  // Clear all cached data
  function clearCache() {
    dataCache.clear()
    loadingPromises.clear()
    monthlyShiftData.value = {}
    monthlyTimeEntryData.value = {}
    monthlyOBHours.value = {}
    monthlySickLeaves.value = {}
    monthlyWorkDays.value = {}
    userTaxCache.value = {}
  }

  // 🚀 OPTIMIZATION: Load all payroll data with request deduplication
  async function loadMonthlyData(usersList = [], forceReload = false) {
    if (!usersList.length) return
    
    const year = selectedMonth.value.getFullYear()
    const month = selectedMonth.value.getMonth() + 1
    const cacheKey = `${year}-${month}`
    
    // Force reload if requested (for month changes)
    if (forceReload) {
      dataCache.delete(cacheKey)
      loadingPromises.delete(cacheKey)
    }
    
    // Check if we're already loading this month's data
    if (loadingPromises.has(cacheKey)) {
      return await loadingPromises.get(cacheKey)
    }
    
    // Create the loading promise
    const loadingPromise = (async () => {
      // Store users for calculations
      users.value = usersList
      
      loading.value = true
      
      try {
        // Check cache first
        if (dataCache.has(cacheKey)) {
          const cachedData = dataCache.get(cacheKey)
          monthlyOBHours.value = cachedData.obHours
          monthlyShiftData.value = cachedData.shiftData
          monthlyTimeEntryData.value = cachedData.timeEntryData
          monthlySickLeaves.value = cachedData.sickLeaves
          monthlyWorkDays.value = cachedData.workDays
          return
        }
        
        // Load data in parallel for all users
        await Promise.all([
          loadMonthlyOBHours(usersList, year, month),
          loadMonthlyShiftData(usersList, year, month),
          loadMonthlyTimeEntryData(usersList, year, month),
          loadMonthlySickLeaves(usersList, year, month),
        ])
        

        
               
        // Cache the results
        dataCache.set(cacheKey, {
          obHours: { ...monthlyOBHours.value },
          shiftData: { ...monthlyShiftData.value },
          timeEntryData: { ...monthlyTimeEntryData.value },
          sickLeaves: { ...monthlySickLeaves.value },
          workDays: { ...monthlyWorkDays.value }
        })
        
        // Calculate taxes last (depends on other data)
        await loadUserTaxes(usersList)
        
      } catch (error) {
        console.error('Failed to load monthly payroll data:', error)
      } finally {
        loading.value = false
        loadingPromises.delete(cacheKey) // Clear the promise
      }
    })()
    
    // Store the promise to prevent duplicates
    loadingPromises.set(cacheKey, loadingPromise)
    
    return await loadingPromise
  }
  
  // Load OB hours for all users
  async function loadMonthlyOBHours(users, year, month) {
    const obHoursPromises = users.map(async (user) => {
      try {
        const obData = await obHoursApi.getMonthlyOBHours(user.id, year, month)
        
        if (obData.monthlyTotal) {
          return { userId: user.id, data: obData.monthlyTotal }
        } else if (obData.results && Array.isArray(obData.results)) {
          const total = obData.results.reduce((acc, day) => ({
            ob1_hours: acc.ob1_hours + (day.ob1_hours || 0),
            ob2_hours: acc.ob2_hours + (day.ob2_hours || 0),
            ob3_hours: acc.ob3_hours + (day.ob3_hours || 0),
            total_hours: acc.total_hours + (day.total_hours || 0)
          }), { ob1_hours: 0, ob2_hours: 0, ob3_hours: 0, total_hours: 0 })
          return { userId: user.id, data: total }
        }
      } catch (error) {
        console.warn(`Failed to load OB hours for user ${user.id}:`, error)
        return { userId: user.id, data: { ob1_hours: 0, ob2_hours: 0, ob3_hours: 0, total_hours: 0 } }
      }
    })
    
    const results = await Promise.all(obHoursPromises)
    const obHoursMap = {}
    results.forEach(result => {
      if (result) {
        obHoursMap[result.userId] = result.data
      }
    })
    
    monthlyOBHours.value = obHoursMap
  }
  
  // Load assigned shifts organized by date for all users
  async function loadMonthlyShiftData(users, year, month) {
    const shiftDataMap = {}
    
    for (const user of users) {
      try {
        const userShiftData = {}
        
        // Load assigned shifts for the user
        const startDate = new Date(year, month - 1, 1)  // month-1 because JS months are 0-indexed
        const endDate = new Date(year, month - 1, 31) // Last day of the month
        
        let assignments = null
        try {
          assignments = await shiftApi.getWorkerShiftsInRange(user.id, startDate, endDate)
        } catch (apiError) {
          assignments = []
        }
        
        if (assignments && Array.isArray(assignments)) {
          assignments.forEach(assignment => {
            const shift = assignment.shift_details || assignment.shift
            
            if (shift && shift.start_time && shift.end_time) {
              const shiftStart = new Date(shift.start_time)
              
              // FILTER BY MONTH - only include shifts in the current month
              const shiftMonth = shiftStart.getMonth() + 1  // 1-indexed
              const shiftYear = shiftStart.getFullYear()
              
              if (shiftYear === year && shiftMonth === month) {
                const dateKey = shift.start_time.split('T')[0] // Get YYYY-MM-DD format
                
                if (!userShiftData[dateKey]) {
                  userShiftData[dateKey] = []
                }
                
                // Calculate required break duration based on shift length
                let startTime = shift.start_time.includes('T') ? shift.start_time.split('T')[1].substring(0, 5) : shift.start_time
                let endTime = shift.end_time.includes('T') ? shift.end_time.split('T')[1].substring(0, 5) : shift.end_time
                const requiredBreak = calculateRequiredBreak(startTime, endTime)
                
                // Add shift in the same format as TimeReporting expects with calculated break
                userShiftData[dateKey].push({
                  start_time: shift.start_time,
                  end_time: shift.end_time,
                  break_duration: requiredBreak,
                  id: shift.id
                })
              }
            }
          })
        }
        
        shiftDataMap[user.id] = userShiftData
      } catch (error) {
        shiftDataMap[user.id] = {}
      }
    }
    
    monthlyShiftData.value = shiftDataMap
  }
  
  // Load time entries organized by date for all users
  async function loadMonthlyTimeEntryData(users, year, month) {
    const timeEntryDataMap = {}
    
    for (const user of users) {
      try {
        const userTimeEntryData = {}
        
        // Get time entries for the current month
        const startDateStr = `${year}-${String(month).padStart(2, '0')}-01`
        const endDateStr = `${year}-${String(month).padStart(2, '0')}-31`
        
        const timeEntries = await timeEntryApi.getTimeEntries({
          worker: user.id,
          start_date: startDateStr,
          end_date: endDateStr
        })
        
        if (timeEntries && Array.isArray(timeEntries)) {
          timeEntries.forEach(entry => {
            if (entry.date && entry.start_time && entry.end_time && entry.status === 'approved') {
              const entryDate = entry.date
              const entryMonth = parseInt(entryDate.split('-')[1])
              const entryYear = parseInt(entryDate.split('-')[0])
              
              // Only count entries from the current month/year
              if (entryYear === year && entryMonth === month) {
                const dateKey = entryDate // Already in YYYY-MM-DD format
                
                if (!userTimeEntryData[dateKey]) {
                  userTimeEntryData[dateKey] = []
                }
                
                // Calculate totalHours using unified time calculations
                let startTime, endTime
                if (entry.start_time.includes('T')) {
                  startTime = entry.start_time.split('T')[1].substring(0, 5) // Get HH:MM
                  endTime = entry.end_time.split('T')[1].substring(0, 5) // Get HH:MM
                } else {
                  startTime = entry.start_time.substring(0, 5) // Get HH:MM
                  endTime = entry.end_time.substring(0, 5) // Get HH:MM
                }
                
                // Use stored break_duration if available, otherwise calculate required break
                const breakDuration = entry.break_duration !== undefined && entry.break_duration !== null 
                  ? entry.break_duration 
                  : calculateRequiredBreak(startTime, endTime)
                
                // Use unified calculateTotalHours function with proper break deduction
                const totalHours = parseFloat(calculateTotalHours(startTime, endTime, breakDuration))
                
                // Add entry in the same format as TimeReporting expects
                userTimeEntryData[dateKey].push({
                  totalHours: formatQuarterHours(totalHours),
                  start_time: startTime,
                  end_time: endTime,
                  break_duration: breakDuration,
                  id: entry.id
                })
              }
            }
          })
        }
        
        timeEntryDataMap[user.id] = userTimeEntryData
      } catch (error) {
        timeEntryDataMap[user.id] = {}
      }
    }
    
    monthlyTimeEntryData.value = timeEntryDataMap
  }
  
  // Load sick leaves for all users
  async function loadMonthlySickLeaves(users, year, month) {
    const sickLeavesMap = {}
    const workDaysMap = {}
    
    for (const user of users) {
      try {
        // Load sick leaves
        const userSickLeaves = await sickLeaveApi.getSickLeaves({
          worker_id: user.id,
          start_date: `${year}-${String(month).padStart(2, '0')}-01`,
          end_date: `${year}-${String(month).padStart(2, '0')}-31`
        })
        
        const filteredSickLeaves = userSickLeaves.filter(sl => sl.worker === user.id)
        sickLeavesMap[user.id] = filteredSickLeaves
        
        // Load work days - we'll use the shift data we already loaded
        const userShiftData = monthlyShiftData.value[user.id] || {}
        const userWorkDays = Object.keys(userShiftData)
        workDaysMap[user.id] = userWorkDays
        
      } catch (error) {
        console.warn(`Could not load sick leaves for user ${user.id}:`, error)
        sickLeavesMap[user.id] = []
        workDaysMap[user.id] = []
      }
    }
    
    monthlySickLeaves.value = sickLeavesMap
    monthlyWorkDays.value = workDaysMap
  }
  

  
  // Load tax calculations for all users
  async function loadUserTaxes(users) {
    const promises = users.map(async (user) => {
      try {
        const grossSalary = getUserGrossSalary(user.id)
        if (grossSalary > 0) {
          const taxResult = await calculatePreliminaryTax({
            grossSalary,
            year: selectedMonth.value.getFullYear(),
            taxTable: user.payroll?.employee_tax_table || 30,
            paymentPeriod: '30B'
          })
          
          const cacheKey = `${selectedMonth.value.getFullYear()}-${selectedMonth.value.getMonth()}`
          if (!userTaxCache.value[user.id]) {
            userTaxCache.value[user.id] = {}
          }
          userTaxCache.value[user.id][cacheKey] = {
            grossSalary,
            tax: taxResult.preliminaryTax
          }
        }
      } catch (error) {
        console.warn(`Failed to calculate tax for user ${user.id}:`, error)
      }
    })
    
    await Promise.all(promises)
  }
  
  // Change selected month and reload data
  async function setSelectedMonth(newDate, usersList = []) {
    // Clear existing data first to ensure reactivity
    clearMonthlyData()
    
    // Set new month (this will trigger computed properties to recalculate)
    selectedMonth.value = new Date(newDate)
    
    // Force reload data for the new month (bypassing cache)
    await loadMonthlyData(usersList, true)
  }
  
  // Clear monthly data to trigger reactivity
  function clearMonthlyData() {
    monthlyShiftData.value = {}
    monthlyTimeEntryData.value = {}
    monthlyOBHours.value = {}
    monthlySickLeaves.value = {}
    monthlyWorkDays.value = {}
    userTaxCache.value = {}
  }
  
  // Format currency
  function formatCurrency(amount) {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }
  
  return {
    // State
    selectedMonth,
    loading,
    monthlyShiftData,
    monthlyTimeEntryData,
    monthlyAssignedHours,
    monthlyReportedHours,
    monthlyOBHours,
    monthlySickLeaves,
    monthlyWorkDays,
    userTaxCache,
    obCoefficients,
    users,
    
    // Salary calculation functions
    getUserBaseSalary,
    getUserGrossSalary,
    getUserEmployeeTax,
    getUserEmployerTax,
    getUserNetSalary,
    getUserOBHours,
    
    // Actions
    loadMonthlyData,
    setSelectedMonth,
    clearCache,
    clearMonthlyData,
    formatCurrency,
    
    // Testing functions
    testUnifiedCalculation,
    testMonthChange,
    testStep2_FetchReportedHours
  }
})